import React, { useState, useEffect } from "react";
import { jsPDF } from "jspdf";

// ─── Free, public, always-on tool — no access control ─────────────────────────

const C = {
  navy:"#0F1F3D", navyMid:"#1B2A4A", navyLt:"#243558",
  gold:"#C9A84C", goldLt:"#F0D98C", white:"#F5F7FA",
  muted:"#8A9BB5", green:"#2ECC8A", red:"#E05C5C",
  orange:"#F2994A", blue:"#4A90D9",
  border:"rgba(201,168,76,0.25)", grid:"rgba(138,155,181,0.15)",
};

const fmt$ = n => "$"+Math.round(n).toLocaleString();
const MANUAL = process.env.PUBLIC_URL + "/Investment_Metrics_User_Guide_v2.pdf";

// ─── Calc engine (mirrors the suite's calcMetrics) ────────────────────────────
function calcMetrics({ purchasePrice, monthlyRent, downPct, mortgageRate,
  mortgageTerm, monthlyExpenses, vacancy, appreciationRate, rentGrowthRate, timeHorizon }) {
  const down = purchasePrice * (downPct/100);
  const loan = purchasePrice - down;
  const mRate = (mortgageRate/100)/12;
  const nPay = mortgageTerm*12;
  const mortgage = mRate>0 ? loan*mRate/(1-Math.pow(1+mRate,-nPay)) : loan/nPay;
  const effRent = monthlyRent*(1-vacancy/100);
  const annRent = effRent*12;
  const annExp = monthlyExpenses*12;
  const annMort = mortgage*12;
  const noi1 = annRent-annExp;
  const cf1 = noi1-annMort;
  const dscr = annMort ? noi1/annMort : 0;
  const capRate = purchasePrice ? (noi1/purchasePrice)*100 : 0;
  const coc = down ? (cf1/down)*100 : 0;
  const grm = monthlyRent*12 ? purchasePrice/(monthlyRent*12) : 0;

  const cashFlows=[], rents=[], rois=[], cumCfArr=[];
  let curRent=monthlyRent, runningCf=0;
  let paybackYear=null;
  for (let yr=1; yr<=timeHorizon; yr++) {
    if (yr>1) curRent *= (1+rentGrowthRate/100);
    const yEff=curRent*(1-vacancy/100);
    const yCF=yEff*12-annExp-annMort;
    runningCf+=yCF;
    const appVal=purchasePrice*((1+appreciationRate/100)**yr-1);
    const roi=down?((runningCf+appVal)/down)*100:0;
    cashFlows.push(Math.round(yCF));
    rents.push(Math.round(curRent*12));
    rois.push(Math.round(roi));
    cumCfArr.push(Math.round(runningCf));
    if (paybackYear===null && roi>=100) paybackYear=yr;
  }

  const saleVal=purchasePrice*((1+appreciationRate/100)**timeHorizon);
  const cfTotal=[...cashFlows];
  cfTotal[cfTotal.length-1]+=saleVal;

  function irrSolve(flows) {
    if(!down || down < 1) return 0;
    const npv=(r)=>flows.reduce((a,cf,j)=>a+cf/Math.pow(1+r,j+1),-down);
    const dnpv=(r)=>flows.reduce((a,cf,j)=>a-(j+1)*cf/Math.pow(1+r,j+2),0);
    for (const guess of [0.1,-0.1,0.5,-0.5,0.01,-0.3,-0.7,-0.9]) {
      let r=guess;
      for(let i=0;i<300;i++){
        const f=npv(r),df=dnpv(r);
        if(!isFinite(f)||!isFinite(df)||Math.abs(df)<1e-12) break;
        const step=f/df; r-=step;
        if(r<-0.9999) r=-0.9999;
        if(Math.abs(step)<1e-8){ const pct=r*100; if(isFinite(pct)&&Math.abs(pct)<9999) return Math.round(pct*100)/100; break; }
      }
    }
    let lo=-0.9999,hi=10.0;
    if(npv(lo)*npv(hi)>0){ lo=-0.9999; hi=-0.0001; if(npv(lo)*npv(hi)>0) return 0; }
    for(let i=0;i<120;i++){ const mid=(lo+hi)/2; if(npv(lo)*npv(mid)<=0) hi=mid; else lo=mid; if(hi-lo<1e-9) break; }
    const pct=((lo+hi)/2)*100;
    return (isFinite(pct)&&Math.abs(pct)<9999) ? Math.round(pct*100)/100 : 0;
  }
  const irrOp=irrSolve(cashFlows);
  const irrTotal=irrSolve(cfTotal);
  const eqMult=down?cfTotal.reduce((a,b)=>a+b,0)/down:0;
  return {capRate,coc,dscr,cf1,irrOp,irrTotal,eqMult,cashFlows,rents,rois,cumCfArr,
    mortgage,down,grm,noi1,paybackYear};
}

// ─── Components ───────────────────────────────────────────────────────────────
const Label = ({children}) => <div style={{fontSize:11,color:C.muted,marginBottom:3}}>{children}</div>;

function SliderInput({label,value,min,max,step=1,display,onChange}) {
  return (
    <div style={{marginBottom:14}}>
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
        <span style={{fontSize:11,color:C.muted}}>{label}</span>
        <span style={{fontSize:12,fontWeight:700,color:C.goldLt}}>{display(value)}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e=>onChange(Number(e.target.value))}
        style={{width:"100%",accentColor:C.gold,cursor:"pointer"}}/>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:C.muted,marginTop:2}}>
        <span>{display(min)}</span><span>{display(max)}</span>
      </div>
    </div>
  );
}

function PlusMinusInput({label,value,min,max,step=1,display,onChange,note}) {
  const [editing,setEditing]=useState(false);
  const [raw,setRaw]=useState("");
  const commit=()=>{ const p=parseFloat(raw.replace(/[$,%\s,]/g,"")); if(!isNaN(p)) onChange(Math.min(max,Math.max(min,p))); setEditing(false); };
  return (
    <div style={{marginBottom:13}}>
      <Label>{label}</Label>
      <div style={{display:"flex",alignItems:"center",gap:6}}>
        <button onClick={()=>onChange(Math.max(min,value-step))}
          style={{width:28,height:28,border:`1px solid ${C.border}`,background:C.navyLt,color:C.gold,borderRadius:5,cursor:"pointer",fontSize:16}}>−</button>
        {editing ? (
          <input type="text" autoFocus value={raw} onChange={e=>setRaw(e.target.value)} onBlur={commit}
            onKeyDown={e=>{ if(e.key==="Enter") commit(); if(e.key==="Escape") setEditing(false); }}
            style={{flex:1,background:C.navyLt,border:`1px solid ${C.gold}`,borderRadius:5,padding:"5px 10px",fontSize:13,color:C.white,textAlign:"center",outline:"none",minWidth:0}}/>
        ) : (
          <div onClick={()=>{setEditing(true);setRaw(String(value));}} title="Click to type"
            style={{flex:1,background:C.navyLt,border:`1px solid ${C.border}`,borderRadius:5,padding:"5px 10px",fontSize:13,color:C.white,textAlign:"center",cursor:"text"}}>
            {display(value)}
          </div>
        )}
        <button onClick={()=>onChange(Math.min(max,value+step))}
          style={{width:28,height:28,border:`1px solid ${C.border}`,background:C.navyLt,color:C.gold,borderRadius:5,cursor:"pointer",fontSize:16}}>+</button>
      </div>
      {note && <div style={{fontSize:10,color:C.muted,marginTop:3}}>{note}</div>}
    </div>
  );
}

function Pill({label,value,color=C.gold,big}) {
  return (
    <div style={{textAlign:"center",padding:big?"14px 16px":"10px 14px",background:C.navyMid,borderRadius:10,border:`1px solid ${C.border}`,flex:big?"1 1 150px":"1 1 110px"}}>
      <div style={{fontSize:10,color:C.muted,textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>{label}</div>
      <div style={{fontSize:big?26:20,fontWeight:700,color,fontFamily:"Georgia,serif"}}>{value}</div>
    </div>
  );
}

function DSCRBar({val}) {
  const pct=Math.min(val/3,1)*100;
  const col=val>=1.25?C.green:val>=1.0?C.gold:C.red;
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:4}}>
        <span style={{color:C.muted}}>DSCR (Debt Service Coverage)</span>
        <span style={{color:col,fontWeight:700}}>{val.toFixed(2)}× {val>=1.25?"✓ Lender OK":val>=1.0?"⚠ Borderline":"✗ Below 1.0"}</span>
      </div>
      <div style={{background:C.navyLt,borderRadius:99,height:7}}>
        <div style={{width:`${pct}%`,height:"100%",background:col,borderRadius:99,transition:"width 0.4s"}}/>
      </div>
    </div>
  );
}

function MultiLineChart({cashFlows,rents,rois,timeHorizon}) {
  const W=560,H=215,PL=62,PR=55,PT=28,PB=32;
  const cW=W-PL-PR,cH=H-PT-PB;
  const allVals=[...cashFlows,...rents];
  const minV=Math.min(...allVals,0),maxV=Math.max(...allVals,1);
  const rangeV=maxV-minV||1;
  const maxROI=Math.max(...rois,1);
  const xPx=i=>PL+(i/(timeHorizon-1||1))*cW;
  const yL=v=>PT+cH-((v-minV)/rangeV)*cH;
  const yR=v=>PT+cH-(v/maxROI)*cH;
  const poly=(data,yFn,col)=><polyline points={data.map((v,i)=>`${xPx(i)},${yFn(v)}`).join(" ")} fill="none" stroke={col} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"/>;
  const ticks=4;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:"auto"}} preserveAspectRatio="xMidYMid meet">
      {[[C.blue,"Multi-Year Cash Flow ($)"],[C.orange,"Projected Rent ($/yr)"],[C.green,"ROI (%)"]].map(([col,lbl],i)=>(
        <g key={i} transform={`translate(${PL+i*168},6)`}>
          <line x1="0" y1="5" x2="16" y2="5" stroke={col} strokeWidth="2.5"/>
          <circle cx="8" cy="5" r="3" fill={col}/>
          <text x="20" y="9" style={{fontSize:9,fill:C.muted}}>{lbl}</text>
        </g>
      ))}
      {Array.from({length:ticks+1},(_,i)=><line key={i} x1={PL} y1={PT+(i/ticks)*cH} x2={W-PR} y2={PT+(i/ticks)*cH} stroke={C.grid} strokeWidth="1" strokeDasharray="3,3"/>)}
      {Array.from({length:ticks+1},(_,i)=>{ const v=minV+((ticks-i)/ticks)*rangeV; return <text key={i} x={PL-4} y={PT+(i/ticks)*cH+4} textAnchor="end" style={{fontSize:9,fill:C.muted}}>{Math.abs(v)>=1000?`$${Math.round(v/1000)}K`:`$${Math.round(v)}`}</text>; })}
      {Array.from({length:ticks+1},(_,i)=>{ const v=((ticks-i)/ticks)*maxROI; return <text key={i} x={W-PR+4} y={PT+(i/ticks)*cH+4} textAnchor="start" style={{fontSize:9,fill:C.muted}}>{Math.round(v)}%</text>; })}
      {cashFlows.map((_,i)=>(i===0||i===timeHorizon-1||(timeHorizon>5&&i%2===0))&&<text key={i} x={xPx(i)} y={H-6} textAnchor="middle" style={{fontSize:9,fill:C.muted}}>{i+1}</text>)}
      <line x1={PL} y1={PT} x2={PL} y2={PT+cH} stroke={C.muted} strokeWidth="1"/>
      <line x1={PL} y1={PT+cH} x2={W-PR} y2={PT+cH} stroke={C.muted} strokeWidth="1"/>
      <text x={PL+cW/2} y={H-1} textAnchor="middle" style={{fontSize:9,fill:C.muted}}>Year</text>
      {poly(cashFlows,yL,C.blue)}{poly(rents,yL,C.orange)}{poly(rois,yR,C.green)}
      {cashFlows.map((v,i)=><circle key={i} cx={xPx(i)} cy={yL(v)} r="3" fill={C.blue}/>)}
      {rents.map((v,i)=><circle key={i} cx={xPx(i)} cy={yL(v)} r="3" fill={C.orange}/>)}
      {rois.map((v,i)=><circle key={i} cx={xPx(i)} cy={yR(v)} r="3" fill={C.green}/>)}
    </svg>
  );
}

// ─── Insights helpers ─────────────────────────────────────────────────────────
function InsightRow({tone,title,body}) {
  const col = tone==="good"?C.green : tone==="warn"?C.gold : tone==="bad"?C.red : C.blue;
  const icon = tone==="good"?"✓" : tone==="warn"?"⚠" : tone==="bad"?"✗" : "•";
  return (
    <div style={{display:"flex",gap:10,padding:"12px 14px",background:C.navyMid,borderRadius:10,
      border:`1px solid ${C.border}`,borderLeft:`3px solid ${col}`,marginBottom:10}}>
      <span style={{color:col,fontWeight:700,fontSize:15,lineHeight:1.3}}>{icon}</span>
      <div>
        <div style={{fontSize:13,fontWeight:700,color:C.white,marginBottom:2}}>{title}</div>
        <div style={{fontSize:12,color:C.muted,lineHeight:1.5}}>{body}</div>
      </div>
    </div>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [tab,setTab] = useState("deal"); // deal | insights
  const [address,setAddress] = useState("");
  const [purchasePrice,setPurchasePrice] = useState(450000);
  const [monthlyRent,setMonthlyRent]     = useState(3800);
  const [monthlyExpenses,setMonthlyExpenses] = useState(950);
  // six sliders
  const [downPct,setDownPct]             = useState(25);
  const [mortgageRate,setMortgageRate]   = useState(6.5);
  const [mortgageTerm,setMortgageTerm]   = useState(30); // stepper, like the original
  const [vacancy,setVacancy]             = useState(5);
  const [appreciationRate,setAppreciationRate] = useState(3);
  const [rentGrowthRate,setRentGrowthRate]     = useState(3);
  const [timeHorizon,setTimeHorizon]     = useState(10);

  const [emailAddr,setEmailAddr] = useState("");
  const [emailSent,setEmailSent] = useState(false);

  const [isNarrow,setIsNarrow] = useState(typeof window!=="undefined" && window.innerWidth<860);
  useEffect(()=>{ const onR=()=>setIsNarrow(window.innerWidth<860); window.addEventListener("resize",onR); return ()=>window.removeEventListener("resize",onR); },[]);
  useEffect(()=>{
    if(typeof window==="undefined") return;
    const s=document.createElement("script"); s.async=true;
    s.src="https://www.googletagmanager.com/gtag/js?id=G-KK6DGNH7FY";
    document.head.appendChild(s);
    window.dataLayer=window.dataLayer||[];
    window.gtag=function(){window.dataLayer.push(arguments);};
    window.gtag("js",new Date());
    window.gtag("config","G-KK6DGNH7FY");
    window.gtag("event","analyzer_opened",{event_category:"engagement"});
  },[]);

  const base = { purchasePrice, monthlyRent, downPct, mortgageRate, mortgageTerm,
    monthlyExpenses, appreciationRate, rentGrowthRate, timeHorizon };
  const m = calcMetrics({ ...base, vacancy });

  // signature stress test: elevated vacancy scenario
  const stressVac = Math.min(vacancy + 10, 50);
  const mStress = calcMetrics({ ...base, vacancy: stressVac });

  function reportLines(){
    return [
      "SINGLE PROPERTY ANALYSIS",
      address ? "Property: "+address : null,
      "Generated: "+new Date().toLocaleDateString(),
      "---","INPUTS",
      "Purchase Price: "+fmt$(purchasePrice),
      "Expected Monthly Rent: "+fmt$(monthlyRent)+"/mo",
      "Operating Expenses: "+fmt$(monthlyExpenses)+"/mo",
      "Down Payment: "+downPct+"%",
      "Mortgage Rate: "+mortgageRate+"%",
      "Mortgage Term: "+mortgageTerm+" yr",
      "Vacancy Rate: "+vacancy+"%",
      "Appreciation: "+appreciationRate+"%/yr",
      "Rent Growth: "+rentGrowthRate+"%/yr",
      "Time Horizon: "+timeHorizon+" yr",
      "---","RESULTS",
      "Cap Rate: "+m.capRate.toFixed(2)+"%",
      "Cash-on-Cash: "+m.coc.toFixed(2)+"%",
      "IRR (Operational): "+m.irrOp.toFixed(2)+"%",
      "IRR (Total incl. Sale): "+m.irrTotal.toFixed(2)+"%",
      "Equity Multiple: "+m.eqMult.toFixed(2)+"x",
      "DSCR (Year 1): "+m.dscr.toFixed(2),
      "GRM: "+m.grm.toFixed(2),
      "Year 1 NOI: "+fmt$(m.noi1),
      "Year 1 Cash Flow: "+fmt$(m.cf1),
      "Monthly Mortgage: "+fmt$(m.mortgage),
      m.paybackYear ? "Down-payment recouped (ROI≥100%): Year "+m.paybackYear : "Down payment not recouped within "+timeHorizon+" yr",
      "---","realestate-analytics.ai | 925-353-5263",
    ].filter(Boolean);
  }
  // Build a branded PDF entirely in-browser (no backend). Returns the jsPDF doc.
  function buildPdf(){
    const doc = new jsPDF({ unit:"mm", format:"a4" });
    const PW = 210;                       // A4 width mm
    const navy=[15,31,61], gold=[201,168,76], white=[245,247,250];
    const ink=[40,52,74], soft=[120,135,160];
    const fname = "Property_Analysis_"+new Date().toISOString().slice(0,10)+".pdf";

    // Header band
    doc.setFillColor(...navy); doc.rect(0,0,PW,30,"F");
    doc.setTextColor(...gold); doc.setFont("helvetica","bold"); doc.setFontSize(17);
    doc.text("RealEstate-Analytics.ai", 14, 14);
    doc.setTextColor(...white); doc.setFont("helvetica","normal"); doc.setFontSize(12);
    doc.text("Single Property Analysis", 14, 22);
    doc.setTextColor(...gold); doc.setFontSize(9);
    doc.text(new Date().toLocaleDateString(), PW-14, 14, {align:"right"});

    let y = 40;
    if (address){
      doc.setTextColor(...ink); doc.setFont("helvetica","bold"); doc.setFontSize(11);
      doc.text(address, 14, y); y += 8;
    }

    // Metric cards (2 rows x 4)
    const metrics = [
      ["Cap Rate", m.capRate.toFixed(2)+"%"],
      ["Cash-on-Cash", m.coc.toFixed(2)+"%"],
      ["IRR (Total)", m.irrTotal.toFixed(2)+"%"],
      ["Equity Multiple", m.eqMult.toFixed(2)+"x"],
      ["DSCR (Yr 1)", m.dscr.toFixed(2)],
      ["Yr 1 Cash Flow", fmt$(m.cf1)],
      ["Monthly Debt", fmt$(m.mortgage)],
      ["GRM", m.grm.toFixed(2)],
    ];
    const cols=4, cardW=(PW-28-9*(cols-1))/cols, cardH=18, gap=9;
    metrics.forEach((mt,i)=>{
      const cx=14+(i%cols)*(cardW+gap), cy=y+Math.floor(i/cols)*(cardH+6);
      doc.setFillColor(247,248,250); doc.setDrawColor(...gold);
      doc.roundedRect(cx,cy,cardW,cardH,2,2,"FD");
      doc.setTextColor(...soft); doc.setFont("helvetica","normal"); doc.setFontSize(7);
      doc.text(mt[0].toUpperCase(), cx+3, cy+6);
      doc.setTextColor(...navy); doc.setFont("helvetica","bold"); doc.setFontSize(13);
      doc.text(String(mt[1]), cx+3, cy+14);
    });
    y += 2*cardH + 6 + 10;

    // Inputs line
    doc.setTextColor(...soft); doc.setFont("helvetica","normal"); doc.setFontSize(8);
    doc.text(
      ["Price "+fmt$(purchasePrice), "Rent "+fmt$(monthlyRent)+"/mo", "Exp "+fmt$(monthlyExpenses)+"/mo",
       "Down "+downPct+"%", "Rate "+mortgageRate+"%", "Vac "+vacancy+"%",
       "Appr "+appreciationRate+"%", "RentGr "+rentGrowthRate+"%", "Hold "+timeHorizon+"yr"].join("   •   "),
      14, y); y += 8;

    // Cash-flow chart (native jsPDF — with labeled axes, mirrors on-screen chart)
    const cfs=m.cashFlows, n=cfs.length;
    const chX=14, chY=y, chW=PW-28, chH=60;       // taller for axis room
    const padL=20, padR=8, padT=6, padB=12;        // inner margins for labels
    const plotX=chX+padL, plotY=chY+padT;
    const plotW=chW-padL-padR, plotH=chH-padT-padB;
    doc.setDrawColor(225,228,234); doc.setFillColor(250,251,252);
    doc.rect(chX,chY,chW,chH,"FD");
    doc.setTextColor(...navy); doc.setFont("helvetica","bold"); doc.setFontSize(9);
    doc.text(timeHorizon+"-Year Cash Flow Projection", chX+3, chY-2);

    const mn=Math.min(0,...cfs), mx=Math.max(1,...cfs), rng=(mx-mn)||1;
    const px=i=> plotX+(i/((n-1)||1))*plotW;
    const py=v=> plotY+plotH-((v-mn)/rng)*plotH;
    const kfmt=v=> (Math.abs(v)>=1000? (v<0?"-$":"$")+Math.round(Math.abs(v)/1000)+"K" : (v<0?"-$":"$")+Math.round(Math.abs(v)));

    // Y-axis gridlines + $ labels (5 ticks)
    doc.setFont("helvetica","normal"); doc.setFontSize(6.5);
    const ticks=4;
    for(let t=0;t<=ticks;t++){
      const val=mn+(t/ticks)*rng;
      const yy=py(val);
      doc.setDrawColor(228,231,236); doc.setLineWidth(0.15);
      doc.line(plotX,yy,plotX+plotW,yy);
      doc.setTextColor(...soft);
      doc.text(kfmt(val), plotX-1.5, yy+1.2, {align:"right"});
    }
    // Zero baseline emphasized
    doc.setDrawColor(190,196,205); doc.setLineWidth(0.3); doc.line(plotX,py(0),plotX+plotW,py(0));
    // Axes
    doc.setDrawColor(150,160,175); doc.setLineWidth(0.3);
    doc.line(plotX,plotY,plotX,plotY+plotH);            // y-axis
    doc.line(plotX,plotY+plotH,plotX+plotW,plotY+plotH);// x-axis
    // X-axis year labels (1, then every other, then last)
    doc.setTextColor(...soft);
    for(let i=0;i<n;i++){
      if(i===0||i===n-1||i%2===1){
        doc.text(String(i+1), px(i), plotY+plotH+4, {align:"center"});
      }
    }
    doc.text("Year", plotX+plotW/2, chY+chH-1.5, {align:"center"});
    // Y-axis title (rotated)
    doc.setTextColor(...soft); doc.setFontSize(6.5);
    doc.text("Annual Cash Flow", chX+3.5, plotY+plotH/2, {align:"center", angle:90});
    // Data line + points
    doc.setDrawColor(74,144,217); doc.setLineWidth(0.7);
    for(let i=0;i<n-1;i++) doc.line(px(i),py(cfs[i]),px(i+1),py(cfs[i+1]));
    doc.setFillColor(74,144,217);
    for(let i=0;i<n;i++) doc.circle(px(i),py(cfs[i]),0.8,"F");
    y = chY+chH+10;

    // Multi-year table
    doc.setTextColor(...navy); doc.setFont("helvetica","bold"); doc.setFontSize(9);
    doc.text("Year-by-Year Projection", 14, y); y+=5;
    doc.setFillColor(...navy); doc.rect(14,y,PW-28,7,"F");
    doc.setTextColor(...white); doc.setFontSize(8);
    doc.text("Year",18,y+5); doc.text("Cash Flow",60,y+5,{align:"right"});
    doc.text("Projected Rent",110,y+5,{align:"right"}); doc.text("ROI %",150,y+5,{align:"right"});
    y+=7;
    doc.setFont("helvetica","normal");
    m.cashFlows.forEach((cf,i)=>{
      if (y>280){ doc.addPage(); y=20; }
      if (i%2===0){ doc.setFillColor(244,246,249); doc.rect(14,y,PW-28,6,"F"); }
      doc.setTextColor(...ink);
      doc.text(String(i+1),18,y+4.2);
      doc.setTextColor(cf>=0?34:200, cf>=0?160:60, cf>=0?120:60);
      doc.text(fmt$(cf),60,y+4.2,{align:"right"});
      doc.setTextColor(...ink);
      doc.text(fmt$(m.rents[i]),110,y+4.2,{align:"right"});
      doc.text(m.rois[i]+"%",150,y+4.2,{align:"right"});
      y+=6;
    });

    // Footer
    y = Math.max(y+8, 285);
    doc.setDrawColor(...gold); doc.setLineWidth(0.3); doc.line(14,y,PW-14,y);
    doc.setTextColor(...soft); doc.setFont("helvetica","normal"); doc.setFontSize(7);
    doc.text("realestate-analytics.ai  |  925-353-5263  |  Educational screening only — not investment, tax, or legal advice. Verify all figures.", 14, y+4);
    return { doc, fname };
  }

  function downloadReport(){
    const { doc, fname } = buildPdf();
    doc.save(fname);
    if(window.gtag) window.gtag("event","analysis_completed",{event_category:"engagement",event_label:"pdf_download"});
  }

  function sendEmail(){
    if(!emailAddr) return;
    // mailto can't carry an attachment, so we drop the text summary in the body
    // AND auto-download the branded PDF so the user can attach it in one step.
    const subject=encodeURIComponent("Single Property Analysis — RealEstate-Analytics.ai");
    const body=encodeURIComponent(
      reportLines().join("\n")+
      "\n\n(A branded PDF of this analysis was just downloaded to your device — attach it to this email.)"+
      "\n\nRun your own numbers: https://maarouni.github.io/singleproperty-public/");
    const { doc, fname } = buildPdf();
    doc.save(fname);
    window.open("mailto:"+emailAddr+"?subject="+subject+"&body="+body);
    setEmailSent(true);
  }

  // ── Sidebar with the 6 sliders + steppers ──
  const Sidebar = (
    <div style={{width:isNarrow?"100%":320,flexShrink:0,background:C.navyMid,borderRadius:14,
      border:`1px solid ${C.border}`,padding:"18px 18px 14px"}}>
      <div style={{marginBottom:13}}>
        <Label>Property Address (optional)</Label>
        <input value={address} onChange={e=>setAddress(e.target.value)} placeholder="123 Main St, City CA 94000"
          style={{width:"100%",boxSizing:"border-box",background:C.navyLt,border:`1px solid ${C.border}`,
            borderRadius:5,padding:"6px 10px",color:C.white,fontSize:12}}/>
      </div>

      <PlusMinusInput label="Purchase Price ($)" value={purchasePrice} min={20000} max={20000000}
        step={10000} display={v=>fmt$(v)} onChange={setPurchasePrice}/>
      <PlusMinusInput label="Expected Monthly Rent ($)" value={monthlyRent} min={0} max={100000}
        step={50} display={v=>fmt$(v)} onChange={setMonthlyRent}
        note={"GRM "+(monthlyRent*12?(purchasePrice/(monthlyRent*12)).toFixed(1):"—")+"× · 1% rule needs "+fmt$(purchasePrice*0.01)+"/mo"}/>
      <PlusMinusInput label="Operating Expenses ($: tax + insurance + misc)" value={monthlyExpenses} min={0} max={50000}
        step={25} display={v=>fmt$(v)} onChange={setMonthlyExpenses}/>

      <div style={{display:"flex",alignItems:"center",gap:6,margin:"16px 0 12px",color:C.gold,fontWeight:700,fontSize:13}}>
        💰 Financing &amp; Growth
      </div>

      {/* The six sliders */}
      <SliderInput label="① Down Payment (%)" value={downPct} min={0} max={100} step={1}
        display={v=>v+"%"} onChange={setDownPct}/>
      <SliderInput label="② Mortgage Rate (%)" value={mortgageRate} min={0} max={15} step={0.125}
        display={v=>(Number.isInteger(v)?v:v.toFixed(3).replace(/0+$/,"").replace(/\.$/,""))+"%"} onChange={setMortgageRate}/>

      <PlusMinusInput label="Mortgage Term (years)" value={mortgageTerm} min={5} max={40}
        step={1} display={v=>v+" yr"} onChange={setMortgageTerm}/>

      <SliderInput label="③ Vacancy Rate (%)" value={vacancy} min={0} max={50} step={1}
        display={v=>v+"%"} onChange={setVacancy}/>
      <SliderInput label="④ Annual Appreciation Rate (%)" value={appreciationRate} min={0} max={10} step={0.5}
        display={v=>v+"%"} onChange={setAppreciationRate}/>
      <SliderInput label="⑤ Annual Rent Growth Rate (%)" value={rentGrowthRate} min={0} max={10} step={0.5}
        display={v=>v+"%"} onChange={setRentGrowthRate}/>
      <SliderInput label="⑥ Investment Time Horizon (Years)" value={timeHorizon} min={2} max={30} step={1}
        display={v=>v+" yr"} onChange={setTimeHorizon}/>

      <a href={MANUAL} target="_blank" rel="noreferrer"
        style={{display:"block",textAlign:"center",marginTop:8,padding:"9px",background:C.navyLt,
          border:`1px solid ${C.border}`,color:C.goldLt,borderRadius:8,textDecoration:"none",fontSize:12,fontWeight:600}}>
        📘 Open User Manual
      </a>
    </div>
  );

  // ── Deal Analyzer tab ──
  const DealTab = (
    <div>
      {address && <div style={{fontSize:13,color:C.goldLt,marginBottom:12}}>📍 {address}</div>}

      <div style={{display:"flex",flexWrap:"wrap",gap:10,marginBottom:12}}>
        <Pill label="Cap Rate" value={m.capRate.toFixed(2)+"%"} color={m.capRate>=6?C.green:C.gold}/>
        <Pill label="Cash-on-Cash" value={m.coc.toFixed(2)+"%"} color={m.coc>=8?C.green:m.coc>=0?C.gold:C.red}/>
        <Pill label="Yr 1 Cash Flow" value={fmt$(m.cf1)} color={m.cf1>=0?C.green:C.red}/>
        <Pill label="Monthly Debt" value={fmt$(m.mortgage)}/>
      </div>

      <div style={{fontSize:13,fontWeight:700,color:C.gold,margin:"6px 0 10px"}}>📈 Long-Term Metrics</div>
      <div style={{display:"flex",flexWrap:"wrap",gap:10,marginBottom:14}}>
        <Pill big label="IRR (Operational)" value={m.irrOp.toFixed(2)+"%"} color={m.irrOp>=0?C.white:C.red}/>
        <Pill big label="IRR (Total incl. Sale)" value={m.irrTotal.toFixed(2)+"%"} color={m.irrTotal>=8?C.green:C.gold}/>
        <Pill big label="Equity Multiple" value={m.eqMult.toFixed(2)+"×"} color={m.eqMult>=2?C.green:C.gold}/>
      </div>

      <div style={{background:C.navyMid,borderRadius:12,border:`1px solid ${C.border}`,padding:"16px 16px 12px",marginBottom:14}}>
        <DSCRBar val={m.dscr}/>
      </div>

      <div style={{background:C.navyMid,borderRadius:12,border:`1px solid ${C.border}`,padding:"14px 14px 8px",marginBottom:14}}>
        <div style={{fontSize:13,fontWeight:700,color:C.gold,marginBottom:6}}>📈 Multi-Year Cash Flow Projection</div>
        <MultiLineChart cashFlows={m.cashFlows} rents={m.rents} rois={m.rois} timeHorizon={timeHorizon}/>
      </div>

      <div style={{display:"flex",flexWrap:"wrap",gap:14}}>
        <div style={{flex:"1 1 240px",background:C.navyMid,borderRadius:10,padding:"16px",border:`1px solid ${C.border}`}}>
          <div style={{fontSize:13,color:C.gold,fontWeight:700,marginBottom:12}}>📊 Save Report</div>
          <button onClick={downloadReport}
            style={{width:"100%",padding:"11px",background:"transparent",border:`1px solid ${C.gold}`,color:C.gold,borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:600,boxSizing:"border-box"}}>
            Download Deal Report (PDF)
          </button>
        </div>
        <div style={{flex:"1 1 240px",background:C.navyMid,borderRadius:10,padding:"16px",border:`1px solid ${C.border}`}}>
          <div style={{fontSize:13,color:C.gold,fontWeight:700,marginBottom:12}}>✉️ Email This Report</div>
          <Label>Enter email address</Label>
          <input type="email" placeholder="you@example.com" value={emailAddr}
            onChange={e=>{setEmailAddr(e.target.value);setEmailSent(false);}}
            style={{width:"100%",boxSizing:"border-box",background:C.navyLt,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 10px",color:C.white,fontSize:13,marginBottom:10}}/>
          <button onClick={sendEmail}
            style={{width:"100%",padding:"11px",background:emailSent?C.green:C.gold,border:"none",color:emailSent?C.white:C.navy,borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:700}}>
            {emailSent?"✓ Email Draft Opened!":"Send Email Report"}
          </button>
        </div>
      </div>
    </div>
  );

  // ── Insights tab ──
  const onePctRent = purchasePrice*0.01;
  const InsightsTab = (
    <div>
      <div style={{fontSize:12,color:C.muted,marginBottom:14,lineHeight:1.5}}>
        Plain-English read on the numbers you've entered. Everything updates as you move the sliders.
      </div>

      <InsightRow tone={m.capRate>=6?"good":m.capRate>=4?"warn":"bad"}
        title={"Cap rate: "+m.capRate.toFixed(2)+"%"}
        body={m.capRate>=6 ? "At or above the ~6% line that's generally considered strong for residential — the property earns well relative to price, before financing."
          : m.capRate>=4 ? "In the moderate 4–6% band. Workable, but you're relying more on financing, appreciation, or rent growth to drive returns."
          : "Below ~4% — thin on an unlevered basis. This deal leans heavily on appreciation and rent growth rather than current income."}/>

      <InsightRow tone={m.cf1>=0?"good":"bad"}
        title={"Year-1 cash flow: "+fmt$(m.cf1)+(m.cf1>=0?" (positive)":" (negative)")}
        body={m.cf1>=0 ? "The property covers its costs and debt in year one with room to spare — that's the foundation of a sustainable hold."
          : "The property runs at a monthly loss in year one. That can still work if you're buying for appreciation, but go in with eyes open and reserves in place."}/>

      <InsightRow tone={m.dscr>=1.25?"good":m.dscr>=1?"warn":"bad"}
        title={"DSCR: "+m.dscr.toFixed(2)+"×"}
        body={m.dscr>=1.25 ? "Above the 1.25× most lenders want to see — income comfortably covers the debt, which helps with financing and leaves a vacancy cushion."
          : m.dscr>=1 ? "Between 1.0× and 1.25× — it covers debt but with little margin. Some lenders will balk; a higher down payment lifts this."
          : "Below 1.0× — income doesn't fully cover the debt at these assumptions. Most lenders won't fund this as-is."}/>

      <InsightRow tone="info"
        title={"1% rule: rent is "+((monthlyRent/onePctRent*100)||0).toFixed(0)+"% of the 1% target"}
        body={monthlyRent>=onePctRent
          ? "Monthly rent clears the "+fmt$(onePctRent)+"/mo screening benchmark (1% of price) — a quick sign the income side is healthy for the price."
          : "To pass the 1% screening rule this property would need about "+fmt$(onePctRent)+"/mo in rent. It's a rough heuristic, not a verdict — many appreciation plays don't pass it."}/>

      <InsightRow tone={m.paybackYear?"good":"warn"}
        title={m.paybackYear ? "You recoup your down payment in ~year "+m.paybackYear : "Down payment not recouped within "+timeHorizon+" years"}
        body={m.paybackYear ? "Counting cumulative cash flow plus appreciation, your initial equity is effectively returned around year "+m.paybackYear+" — then the rest is upside."
          : "Within your "+timeHorizon+"-year horizon, cumulative cash flow plus appreciation doesn't yet equal your down payment. Extend the horizon or revisit price/rent."}/>

      {/* Signature stress test */}
      <div style={{marginTop:16,background:C.navyMid,borderRadius:12,border:`1px solid ${C.border}`,padding:"16px"}}>
        <div style={{fontSize:13,fontWeight:700,color:C.gold,marginBottom:6}}>🧪 Vacancy stress test</div>
        <div style={{fontSize:12,color:C.muted,lineHeight:1.6}}>
          At your current <b style={{color:C.goldLt}}>{vacancy}%</b> vacancy, year-1 cash flow is{" "}
          <b style={{color:m.cf1>=0?C.green:C.red}}>{fmt$(m.cf1)}</b> and cash-on-cash is{" "}
          <b style={{color:C.goldLt}}>{m.coc.toFixed(1)}%</b>.{" "}
          Push vacancy to <b style={{color:C.goldLt}}>{stressVac}%</b> and year-1 cash flow becomes{" "}
          <b style={{color:mStress.cf1>=0?C.green:C.red}}>{fmt$(mStress.cf1)}</b>{" "}
          (cash-on-cash <b style={{color:C.goldLt}}>{mStress.coc.toFixed(1)}%</b>).{" "}
          {mStress.cf1>=0
            ? "The deal still cash-flows under that stress — a sign of resilience."
            : "The deal goes cash-flow negative under that stress — make sure you have reserves for a soft rental market."}
        </div>
        <div style={{fontSize:10,color:C.muted,marginTop:8}}>Tip: drag the Vacancy slider to watch the projection respond in real time on the Deal Analyzer tab.</div>
      </div>
    </div>
  );

  const TabBtn = ({id,children}) => (
    <button onClick={()=>setTab(id)}
      style={{background:"none",border:"none",cursor:"pointer",fontSize:14,fontWeight:700,
        padding:"6px 2px",marginRight:24,color:tab===id?C.gold:C.muted,
        borderBottom:tab===id?`2px solid ${C.gold}`:"2px solid transparent"}}>
      {children}
    </button>
  );

  return (
    <div style={{minHeight:"100vh",background:C.navy,color:C.white,fontFamily:"'Calibri','Segoe UI',sans-serif",padding:isNarrow?"16px 12px 40px":"24px 28px 48px"}}>
      <div style={{maxWidth:1280,margin:"0 auto 16px"}}>
        <div style={{display:"flex",alignItems:"baseline",gap:14,flexWrap:"wrap"}}>
          <h1 style={{fontSize:isNarrow?24:30,fontWeight:700,fontFamily:"Georgia,serif"}}>🏡 Single Property Analyzer</h1>
          <span style={{fontSize:isNarrow?30:40,color:C.green,fontWeight:800,letterSpacing:2,lineHeight:1,textShadow:"0 0 18px rgba(46,204,138,0.45)"}}>FREE</span>
        </div>
        <div style={{fontSize:isNarrow?15:17,color:C.white,marginTop:8,lineHeight:1.5,fontWeight:500}}>
          Enter your numbers, move the sliders, and watch every metric and the cash-flow curve update live.{" "}
          <span style={{color:C.goldLt}}>No login. No signup.</span>
        </div>
      </div>

      {/* Tabs */}
      <div style={{maxWidth:1280,margin:"0 auto",borderBottom:`1px solid ${C.border}`,marginBottom:18}}>
        <TabBtn id="deal">Deal Analyzer</TabBtn>
        <TabBtn id="insights">Insights</TabBtn>
      </div>

      <div style={{maxWidth:1280,margin:"0 auto",display:"flex",flexDirection:isNarrow?"column":"row",gap:isNarrow?16:22,alignItems:"flex-start"}}>
        {Sidebar}
        <div style={{flex:1,minWidth:0}}>
          {tab==="deal" ? DealTab : InsightsTab}
        </div>
      </div>

      <div style={{maxWidth:1280,margin:"26px auto 0",fontSize:14,color:"#ffffff",borderTop:`1px solid ${C.border}`,paddingTop:12}}>
        RealEstate-Analytics.ai · Free educational screening tool · runs entirely in your browser, no data stored · not investment, tax, or legal advice. Verify all figures independently.
      </div>
    </div>
  );
}
